// ── 연차 추가/수정 모달 (단건 수정 + 복수 직원×날짜 신규 추가) ─────────────────
import React from 'react';
import Modal from '../../../components/Modal';
import MultiDatePicker from '../../../components/MultiDatePicker';
import { LEAVE_TYPES_FORM, LEAVE_TYPES_FORM_EDIT, FIXED_COUNT_MAP } from './constants';
import { EffectFields } from './EffectFields';
import {
  outlineBtn, primaryBtn, dangerOutlineBtn,
  inputStyle, formGridStyle, formLabelStyle, formErrorStyle,
  modalSectionStyle, modalSectionTitleStyle, modalSectionCountStyle,
  pickerToggleBtn, employeeGridStyle, employeeChipBtnStyle,
  fixedCountDisplayStyle, previewBadgeStyle,
} from './stylesB';

export function AddEditModal({
  // 모달 제어
  open, onClose,
  editId, save, onDelete,
  // 폼 상태
  form, updateFormWithRules,
  userPickerText, handleUserPickerChange,
  // 신규 추가 전용: 복수 직원×복수 날짜
  pickerUsers,
  selectedUserIds, setSelectedUserIds,
  selectedDates, setSelectedDates,
  showResignedInPicker, setShowResignedInPicker,
  toggleUserSelection,
  // 유효성 파생값
  lockedCategory, categoryOptions,
  halfLeaveThresholdWarning, multiUserHalfLeaveWarning,
  eventTypes = [],
}) {
  return (
    <Modal
      open={open}
      title={editId ? '연차 수정' : '연차 추가'}
      icon={editId ? '✏️' : '📅'}
      onClose={onClose}
      width={editId ? undefined : 'min(680px, 96vw)'}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {/* 수정 모드에만 왼쪽에 삭제 버튼 표시 (모바일 카드에서 삭제 버튼 제거 대체) */}
          <div>
            {editId && onDelete && (
              <button style={dangerOutlineBtn} onClick={onDelete}>삭제</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={outlineBtn} onClick={onClose}>취소</button>
            <button style={primaryBtn} onClick={save}>저장</button>
          </div>
        </div>
      )}
    >
      {editId ? (
        /* ── 수정 모드: 단건 폼 ── */
        <div style={formGridStyle}>
          <div style={{ display: 'grid', gap: 2, gridColumn: '1 / -1' }}>
            <label style={formLabelStyle}>직원</label>
            <input
              style={inputStyle}
              placeholder="이름 검색"
              value={userPickerText}
              onChange={(e) => handleUserPickerChange(e.target.value)}
            />
            {String(userPickerText || '').trim() && !form.user_id && (
              <span style={formErrorStyle}>일치하는 직원명이 없습니다.</span>
            )}
          </div>

          <div style={{ display: 'grid', gap: 2 }}>
            <label style={formLabelStyle}>날짜</label>
            <input
              style={inputStyle}
              type="date"
              value={form.date}
              onChange={(e) => updateFormWithRules({ date: e.target.value })}
            />
          </div>

          <div style={{ display: 'grid', gap: 2 }}>
            <label style={formLabelStyle}>종류</label>
            {/* 수정 모드: 구형 '반차'도 포함하여 기존 레코드 유지 */}
            <select style={inputStyle} value={form.type} onChange={(e) => updateFormWithRules({ type: e.target.value })}>
              {LEAVE_TYPES_FORM_EDIT.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* 반차기준시간 미설정 경고: full-width */}
          {halfLeaveThresholdWarning && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4, padding: '6px 10px' }}>
              ⚠ {halfLeaveThresholdWarning}
            </div>
          )}

          <div style={{ display: 'grid', gap: 2 }}>
            <label style={formLabelStyle}>구분</label>
            <select
              style={inputStyle}
              value={form.category}
              disabled={Boolean(lockedCategory)}
              onChange={(e) => updateFormWithRules({ category: e.target.value })}
            >
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gap: 2 }}>
            <label style={formLabelStyle}>개수</label>
            {/* 연차/반차/경조사는 개수 고정, 나머지는 직접 입력 */}
            {FIXED_COUNT_MAP[form.type] !== undefined ? (
              <div style={fixedCountDisplayStyle}>
                {FIXED_COUNT_MAP[form.type]}
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>(고정)</span>
              </div>
            ) : (
              <input
                style={inputStyle}
                type="number"
                step="0.5"
                value={form.count}
                onChange={(e) => updateFormWithRules({ count: e.target.value }, { notify: false })}
              />
            )}
          </div>

          <div style={{ display: 'grid', gap: 2, gridColumn: '1 / -1' }}>
            <label style={formLabelStyle}>사유</label>
            <input
              style={inputStyle}
              value={form.reason}
              onChange={(e) => updateFormWithRules({ reason: e.target.value }, { notify: false })}
              placeholder="사유 (선택)"
            />
          </div>

          <EffectFields form={form} updateFormWithRules={updateFormWithRules} eventTypes={eventTypes} />
        </div>
      ) : (
        /* ── 신규 추가 모드: 복수 직원 × 복수 날짜 ── */
        <div style={{ display: 'grid', gap: 10 }}>

          {/* 섹션 1: 직원 선택 */}
          <div style={modalSectionStyle}>
            <div style={modalSectionTitleStyle}>
              직원 선택
              {selectedUserIds.length > 0 && (
                <span style={modalSectionCountStyle}>{selectedUserIds.length}명 선택됨</span>
              )}
              {/* 재직중/전체 토글 — 우측 정렬 */}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 3, background: 'var(--bg-subtle)', padding: 2, borderRadius: 8 }}>
                <button type="button" style={pickerToggleBtn(!showResignedInPicker)} onClick={() => setShowResignedInPicker(false)}>
                  재직중
                </button>
                <button type="button" style={pickerToggleBtn(showResignedInPicker)} onClick={() => setShowResignedInPicker(true)}>
                  전체
                </button>
              </div>
            </div>
            {/* 직원 버튼 그리드 — 클릭으로 선택/해제 */}
            <div style={employeeGridStyle}>
              {pickerUsers.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>직원 목록을 불러오는 중...</span>
              )}
              {pickerUsers.map((u) => {
                const isSelected = selectedUserIds.includes(String(u.id));
                return (
                  <button
                    key={u.id}
                    type="button"
                    style={employeeChipBtnStyle(isSelected)}
                    onClick={() => toggleUserSelection(u)}
                  >
                    {u.name}
                  </button>
                );
              })}
            </div>
            {selectedUserIds.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                직원을 한 명 이상 선택해 주세요.
              </div>
            )}
          </div>

          {/* 섹션 2: 날짜 선택 */}
          <div style={modalSectionStyle}>
            <div style={modalSectionTitleStyle}>
              날짜 선택
              <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)', marginLeft: 6 }}>
                Shift+클릭으로 범위 선택
              </span>
              {selectedDates.length > 0 && (
                <span style={modalSectionCountStyle}>{selectedDates.length}일</span>
              )}
            </div>
            <MultiDatePicker selectedDates={selectedDates} onChange={setSelectedDates} />
          </div>

          {/* 섹션 3: 연차 정보 */}
          <div style={modalSectionStyle}>
            <div style={modalSectionTitleStyle}>연차 정보</div>
            <div style={formGridStyle}>
              <div style={{ display: 'grid', gap: 4 }}>
                <label style={formLabelStyle}>종류</label>
                <select style={inputStyle} value={form.type} onChange={(e) => updateFormWithRules({ type: e.target.value })}>
                  {LEAVE_TYPES_FORM.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <label style={formLabelStyle}>구분</label>
                <select
                  style={inputStyle}
                  value={form.category}
                  disabled={Boolean(lockedCategory)}
                  onChange={(e) => updateFormWithRules({ category: e.target.value })}
                >
                  {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <label style={formLabelStyle}>개수</label>
                {FIXED_COUNT_MAP[form.type] !== undefined ? (
                  <div style={fixedCountDisplayStyle}>
                    {FIXED_COUNT_MAP[form.type]}
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>(고정)</span>
                  </div>
                ) : (
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.5"
                    value={form.count}
                    onChange={(e) => updateFormWithRules({ count: e.target.value }, { notify: false })}
                  />
                )}
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <label style={formLabelStyle}>사유 (선택)</label>
                <input
                  style={inputStyle}
                  value={form.reason}
                  onChange={(e) => updateFormWithRules({ reason: e.target.value }, { notify: false })}
                  placeholder="사유"
                />
              </div>
              <EffectFields form={form} updateFormWithRules={updateFormWithRules} eventTypes={eventTypes} />
            </div>
            {/* 반차기준시간 미설정 경고: 선택된 사용자 근무유형 기준 동적 표시 */}
            {multiUserHalfLeaveWarning && (
              <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4, padding: '6px 10px', marginTop: 4 }}>
                ⚠ {multiUserHalfLeaveWarning}
              </div>
            )}
          </div>

          {/* 미리보기 뱃지 */}
          {selectedUserIds.length > 0 && selectedDates.length > 0 && (
            <div style={previewBadgeStyle}>
              <span style={{ color: '#1d4ed8' }}>{selectedUserIds.length}명</span>
              {' × '}
              <span style={{ color: '#7c3aed' }}>{selectedDates.length}일</span>
              {' = '}
              <strong style={{ fontSize: 15 }}>{selectedUserIds.length * selectedDates.length}건</strong>
              {' 추가 예정'}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
